import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Calendar,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  Calculator,
  Receipt,
  User,
  Building,
  ArrowRight
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { PayslipWithLineItems, Payslip } from '@/services/payslips/types';

interface PayslipSummaryCardProps {
  payslip: PayslipWithLineItems;
  showSensitiveData?: boolean;
  previousPayslip?: Payslip;
}

export function PayslipSummaryCard({
  payslip,
  showSensitiveData = true,
  previousPayslip
}: PayslipSummaryCardProps) {
  const { currencyCode } = useWorkplaceCurrency();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // Calculate period information
  const periodDays = differenceInDays(
    new Date(payslip.end_date),
    new Date(payslip.start_date)
  ) + 1;

  const totalHours = Number(payslip.total_hours);
  
  // Get calculated totals or fallback to basic payslip data
  const grossEarnings = payslip.calculated_totals?.gross_earnings || Number(payslip.gross_pay);
  const totalDeductions = payslip.calculated_totals?.total_deductions || 0;
  const netPay = payslip.calculated_totals?.net_pay || Number(payslip.net_pay || 0);
  
  // Calculate hourly rate estimate
  const estimatedHourlyRate = totalHours > 0 ? grossEarnings / totalHours : 0;
  
  // Comparison with previous payslip
  const previousGross = previousPayslip ? Number(previousPayslip.gross_pay) : 0;
  const previousHours = previousPayslip ? Number(previousPayslip.total_hours) : 0;
  
  const grossChange = previousGross > 0 ? ((grossEarnings - previousGross) / previousGross) * 100 : 0;
  const hoursChange = previousHours > 0 ? ((totalHours - previousHours) / previousHours) * 100 : 0;

  // Calculate deduction percentages
  const deductionPercentage = grossEarnings > 0 ? (totalDeductions / grossEarnings) * 100 : 0;
  const netPercentage = grossEarnings > 0 ? (netPay / grossEarnings) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Pay Period Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Pay Period Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Period</span>
              </div>
              <p className="font-semibold">
                {format(new Date(payslip.start_date), 'MMM dd')} - {format(new Date(payslip.end_date), 'MMM dd, yyyy')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {periodDays} day{periodDays !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Hours Worked</span>
              </div>
              <p className="font-semibold text-xl">{totalHours.toFixed(1)}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Est. Hourly Rate</span>
              </div>
              <p className="font-semibold text-xl">
                {showSensitiveData ? formatCurrency(estimatedHourlyRate) : '****'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Based on total pay
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pay Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Pay Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Calculation Flow */}
          <div className="space-y-4">
            {/* Gross Earnings */}
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950 rounded-lg">
              <div className="flex items-center gap-3">
                <DollarSign className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-900 dark:text-green-100">Gross Earnings</p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Total earnings before deductions
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-green-600">
                  {showSensitiveData ? formatCurrency(grossEarnings) : '****'}
                </p>
                {previousPayslip && (
                  <div className="flex items-center gap-1 text-sm mt-1">
                    {grossChange >= 0 ? (
                      <TrendingUp className="h-3 w-3 text-green-600" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-red-600" />
                    )}
                    <span className={grossChange >= 0 ? "text-green-600" : "text-red-600"}>
                      {formatPercentage(grossChange)} vs last period
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Deduction Arrow */}
            {totalDeductions > 0 && (
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Minus className="h-4 w-4" />
                  <span className="text-sm">Deductions</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            )}

            {/* Total Deductions */}
            {totalDeductions > 0 && (
              <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <div className="flex items-center gap-3">
                  <Minus className="h-6 w-6 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-900 dark:text-red-100">Total Deductions</p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {deductionPercentage.toFixed(1)}% of gross earnings
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-red-600">
                    {showSensitiveData ? formatCurrency(totalDeductions) : '****'}
                  </p>
                </div>
              </div>
            )}

            {/* Equals Arrow */}
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="text-sm font-medium">=</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>

            {/* Net Pay */}
            <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border-2 border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-3">
                <Receipt className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900 dark:text-blue-100">Net Pay</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {netPercentage.toFixed(1)}% of gross earnings
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">
                  {showSensitiveData ? formatCurrency(netPay) : '****'}
                </p>
                <Badge variant="outline" className="mt-1">
                  Take Home
                </Badge>
              </div>
            </div>
          </div>

          {/* Detailed Breakdown if line items exist */}
          {payslip.line_items && payslip.line_items.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Detailed Components
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Earnings Breakdown */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      <span className="font-medium">Earnings Components</span>
                    </div>
                    {payslip.line_items
                      .filter(item => item.category === 'earnings')
                      .slice(0, 3)
                      .map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{item.description}</span>
                          <span className="font-mono">
                            {showSensitiveData ? formatCurrency(Number(item.gross_amount)) : '****'}
                          </span>
                        </div>
                      ))}
                    {payslip.line_items.filter(item => item.category === 'earnings').length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        +{payslip.line_items.filter(item => item.category === 'earnings').length - 3} more items
                      </div>
                    )}
                  </div>

                  {/* Deductions Breakdown */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Minus className="h-4 w-4 text-red-600" />
                      <span className="font-medium">Deduction Components</span>
                    </div>
                    {payslip.line_items
                      .filter(item => ['deductions', 'taxes', 'benefits'].includes(item.category || ''))
                      .slice(0, 3)
                      .map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{item.description}</span>
                          <span className="font-mono">
                            {showSensitiveData ? formatCurrency(Number(item.gross_amount)) : '****'}
                          </span>
                        </div>
                      ))}
                    {payslip.line_items.filter(item => ['deductions', 'taxes', 'benefits'].includes(item.category || '')).length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        +{payslip.line_items.filter(item => ['deductions', 'taxes', 'benefits'].includes(item.category || '')).length - 3} more items
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Comparison with Previous Period */}
      {previousPayslip && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Period Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Earnings Change</p>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {grossChange >= 0 ? (
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  )}
                  <span className={cn(
                    "text-xl font-bold",
                    grossChange >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {formatPercentage(grossChange)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Previous: {showSensitiveData ? formatCurrency(previousGross) : '****'}</div>
                  <div>Current: {showSensitiveData ? formatCurrency(grossEarnings) : '****'}</div>
                </div>
              </div>

              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Hours Change</p>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {hoursChange >= 0 ? (
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-orange-600" />
                  )}
                  <span className={cn(
                    "text-xl font-bold",
                    hoursChange >= 0 ? "text-blue-600" : "text-orange-600"
                  )}>
                    {formatPercentage(hoursChange)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Previous: {previousHours.toFixed(1)} hrs</div>
                  <div>Current: {totalHours.toFixed(1)} hrs</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}